/**
 * Namespace Cards Component
 *
 * Displays cache namespace summaries in a card grid.
 * Allows selecting a namespace for detailed operations.
 */

import { Database, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { CacheNamespaceInfo, CacheNamespace } from '@/types/cache';
import { CACHE_NAMESPACE_COLORS, formatTTL } from '@/types/cache';

interface NamespaceCardsProps {
  namespaces: CacheNamespaceInfo[];
  selectedNamespace: CacheNamespace | null;
  onSelect: (namespace: CacheNamespace) => void;
  onInvalidate: (namespace: CacheNamespace) => void;
  isInvalidating: boolean;
}

/**
 * Format key count with commas
 */
function formatCount(count: number): string {
  return count.toLocaleString();
}

/**
 * Namespace Cards Grid
 * Shows all cache namespaces with counts and actions
 */
export function NamespaceCards({
  namespaces,
  selectedNamespace,
  onSelect,
  onInvalidate,
  isInvalidating,
}: NamespaceCardsProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {namespaces.map(ns => (
        <Card
          key={ns.name}
          className={`cursor-pointer transition-colors hover:border-primary ${
            selectedNamespace === ns.name ? 'border-primary ring-2 ring-primary/20' : ''
          }`}
          onClick={() => onSelect(ns.name)}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Badge variant="outline" className={CACHE_NAMESPACE_COLORS[ns.name]}>
                {ns.displayName}
              </Badge>
              <Database className="h-4 w-4 text-muted-foreground" />
            </div>
            <CardTitle className="text-2xl font-bold mt-2">{formatCount(ns.count)}</CardTitle>
            <CardDescription className="text-xs">{ns.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">TTL:</span> {formatTTL(ns.ttlSeconds)}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={e => {
                  e.stopPropagation();
                  onInvalidate(ns.name);
                }}
                disabled={isInvalidating || ns.count === 0}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div
              className="text-xs text-muted-foreground mt-1 font-mono truncate"
              title={ns.pattern}
            >
              {ns.pattern}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
